'use client';

import { useEffect, useId, useRef, useState } from 'react';

import Link from 'next/link';

import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Dumbbell,
  Flower2,
  GraduationCap,
  IdCard,
  Stethoscope,
  Trophy,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import { localizedHref } from '~/lib/marketing/routes';
import type { MarketingLocale } from '~/lib/marketing/site';

import {
  SOLUTIONS,
  SOLUTIONS_MENU,
  SOLUTIONS_SECONDARY,
  type SolutionSlug,
} from '../_lib/solutions.content';

export const SOLUTION_ICONS: Record<SolutionSlug, typeof Trophy> = {
  'padel-tennis': Trophy,
  studios: Flower2,
  clinics: Stethoscope,
  gyms: Dumbbell,
  'membership-clubs': IdCard,
  enterprise: Building2,
};

/** Primary audiences followed by the secondary ones, in menu order. */
const MENU_ITEMS = [
  ...SOLUTIONS.map((solution) => ({
    key: solution.slug,
    href: solution.href,
    label: solution.menuLabel,
    description: solution.menuDescription,
    Icon: SOLUTION_ICONS[solution.slug],
  })),
  ...SOLUTIONS_SECONDARY.map((item) => ({
    key: item.href,
    href: item.href,
    label: item.label,
    description: item.description,
    Icon: GraduationCap,
  })),
];

/**
 * Desktop "Solutions" disclosure for the marketing header. Opens on click or
 * hover, closes on Escape, outside click, or picking a link.
 */
export function SolutionsMenu({
  locale,
  solid,
}: {
  locale: MarketingLocale;
  solid: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | null>(null);
  const panelId = useId();

  // Document-level listeners are the only way to detect clicks outside the
  // menu and Escape presses while focus sits elsewhere.
  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // The focused link unmounts with the panel; hand focus back to the
        // trigger so keyboard users resume where they were.
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 160);
  };

  return (
    // Not `relative`: the panel positions against the fixed header, so it is
    // centred on the page instead of on the trigger.
    <div
      ref={rootRef}
      // Close when keyboard focus moves past the last link.
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          cancelClose();
          setOpen(true);
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          scheduleClose();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(event) => {
          // A mouse click on a panel that hover already opened should not
          // snap it shut; keyboard and touch activation still toggle.
          const viaMouse = event.nativeEvent instanceof PointerEvent && event.nativeEvent.pointerType === 'mouse';

          setOpen((value) => (viaMouse && value ? true : !value));
        }}
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap font-roboto-mono text-sm uppercase tracking-[1px] transition-colors duration-300',
          solid
            ? 'text-[#202020]/80 hover:text-[#202020]'
            : 'text-white/90 hover:text-white',
        )}
      >
        {SOLUTIONS_MENU.trigger[locale]}
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={panelId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 top-full z-50 mx-auto w-[900px] max-w-[calc(100%-2rem)] pt-2"
          >
            <div className="overflow-hidden rounded-[20px] border border-[#dcd7cf] bg-white text-start shadow-[0_30px_60px_-20px_rgba(32,32,32,0.35)]">
              <ul className="grid grid-cols-3 gap-1 p-3">
                {MENU_ITEMS.map(({ key, href, label, description, Icon }) => (
                  <li key={key}>
                    <Link
                      href={localizedHref(href, locale)}
                      onClick={() => setOpen(false)}
                      className="group flex h-full gap-3 rounded-[14px] p-3 transition-colors hover:bg-[#f7f6f4]"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#ede8e2] text-[#202020] transition-colors group-hover:bg-[#202020] group-hover:text-white">
                        <Icon className="h-[18px] w-[18px]" />
                      </span>
                      <span>
                        <span className="block font-roboto text-[15px] font-medium text-[#202020]">
                          {label[locale]}
                        </span>
                        <span className="mt-0.5 block font-roboto text-[13px] leading-snug text-[#2b2b2b]/60">
                          {description[locale]}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-end border-t border-[#ebe7e1] bg-[#f7f6f4] px-6 py-3.5">
                <Link
                  href={localizedHref('/customers', locale)}
                  onClick={() => setOpen(false)}
                  className="group inline-flex items-center gap-1.5 font-roboto-mono text-[11px] uppercase tracking-wide text-[#202020]"
                >
                  {SOLUTIONS_MENU.viewCustomers[locale]}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Flat solutions list for the mobile menu (no nested disclosure). */
export function SolutionsMobileList({
  locale,
  onNavigate,
}: {
  locale: MarketingLocale;
  onNavigate: () => void;
}) {
  return (
    <div className="border-b border-[#202020]/10 py-3">
      <p className="mb-2 font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c]">
        {SOLUTIONS_MENU.trigger[locale]}
      </p>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {MENU_ITEMS.map(({ key, href, label, Icon }) => (
          <li key={key}>
            <Link
              href={localizedHref(href, locale)}
              onClick={onNavigate}
              className="flex min-h-11 items-center gap-3 rounded-[12px] py-1.5 text-[15px] text-[#202020]"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-white text-[#202020]">
                <Icon className="h-4 w-4" />
              </span>
              {label[locale]}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
